<template>
  <div class="wrapper">
    <MglMap
      v-if="disable"
      :accessToken="accessToken"
      :center="center"
      :zoom.sync="zoom"
      :mapStyle="mapStyle"
      @load="onMapLoaded"
      @dragend="filterLocationList()"
      :keyboard="false"
    >
      <MglNavigationControl ref="controls" position="top-right" />
      <CampaignModalPageGeoMapMarkerContainer
        v-if="locationListInFrame.length < 100"
        :point-list="locationListInFrame"
        :selected-location-groups="selectedLocationGroups"
      />
    </MglMap>
    <transition name="slide-up">
      <div v-if="locationListInFrame.length >= 100" class="footer">
        <div class="red--text">
          地図の範囲内に地点数が100地点以上あるため、表示できません。ズームをして、表示エリアを狭めて下さい。
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { minBy, maxBy } from 'lodash/math'
import CampaignModalPageGeoMapMarkerContainer from './CampaignModalPageGeoMapMarkerContainer.vue'
import { MglMap, MglNavigationControl } from 'vue-mapbox'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import { LocationRepository } from '@/models/Location'
import { mapGetters, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import { locationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'

export default {
  name: 'CampaignModalPageGeoMap',
  components: {
    MglMap,
    MglNavigationControl,
    CampaignModalPageGeoMapMarkerContainer,
  },
  props: {
    deliveryLocationGroups: {
      type: Array,
      default: () => [],
    },
    selectedLocationGroups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      accessToken:
        'pk.eyJ1Ijoid25vZ3VjaGkiLCJhIjoiY2lqY2c2dmxuMDAxZHUxamJwMGl3ZnV5ciJ9.WBilN5epB7RdyO-JbUQd2A',
      mapStyle: 'mapbox://styles/wnoguchi/ckbu3bvc303t41ip4yzmywi29',
      zoom: 17,
      locationList: [],
      locationListInFrame: [],
      mapLocationList: [],
      disable: false,
    }
  },
  computed: {
    ...mapGetters('campaign', ['getLocationsById']),
    center() {
      return [139.7009177, 35.6580971]
    },
    selectedLocationList() {
      const selectedLocationGroupIds = this.selectedLocationGroups.map(
        group => group.locationGroupId,
      )
      return this.mapLocationList.filter(location =>
        selectedLocationGroupIds.includes(location.locationGroupId),
      )
    },
  },
  watch: {
    zoom() {
      this.filterLocationList()
    },
    async deliveryLocationGroups() {
      await this.fetchLocations()
      this.map && this.fitBounds()
      this.disable = true
    },
    locationList: {
      handler() {
        this.filterLocationList()
        this.fitBounds()
        this.setLocationListCount(this.locationList.length)
      },
      deep: true,
    },
    selectedLocationList() {
      this.fitBounds()
    },
  },
  created() {
    this.map = null
  },
  beforeDestroy() {
    this.$refs.controls && this.$refs.controls.$destroy()
  },
  methods: {
    ...mapActions('campaign', {
      setLocationListCount: 'SET_LOCATION_LIST_COUNT',
    }),
    async onMapLoaded(event) {
      this.map = event.map

      const language = new MapboxLanguage()
      this.map.setStyle(language.setLanguage(this.map.getStyle(), 'ja'))

      this.map.resize()
      await this.fetchLocations()
      this.fitBounds()
    },
    filterLocationList: debounce(function() {
      if (!this.map) return
      const frame = this.map.getBounds()
      this.locationListInFrame = this.mapLocationList.filter(point => {
        // 表示域内の地点をフィルター
        return (
          (point.shape.longitude - frame._ne.lng) *
            (point.shape.longitude - frame._sw.lng) <=
            0 &&
          (point.shape.latitude - frame._ne.lat) *
            (point.shape.latitude - frame._sw.lat) <=
            0
        )
      })
    }),
    async fitBounds() {
      if (!this.map) return
      const selectedLocationList = this.selectedLocationList
      const locationList =
        selectedLocationList.length > 0
          ? selectedLocationList
          : this.mapLocationList
      if (locationList.length === 0) return
      if (locationList.length === 1) {
        // 地点が1つだけの場合は中心にする
        const point = locationList[0]
        const { longitude, latitude } = point.shape
        this.map.flyTo({ center: [longitude, latitude], zoom: 17 })
        return
      }

      await this.$nextTick()
      const lngLats = locationList.map(point => ({
        lng: point.shape.longitude,
        lat: point.shape.latitude,
      }))
      const maxLng = maxBy(lngLats, lngLat => lngLat.lng)
      const minLng = minBy(lngLats, lngLat => lngLat.lng)
      const maxLat = maxBy(lngLats, lngLat => lngLat.lat)
      const minLat = minBy(lngLats, lngLat => lngLat.lat)

      const padding = 50

      // 南西と東北の座標を指定して全ての地点が入る矩形を指定する
      this.map.fitBounds(
        [
          [minLng.lng, minLat.lat],
          [maxLng.lng, maxLat.lat],
        ],
        {
          padding,
        },
      )
    },
    async fetchLocations() {
      let locationList = []
      let mapLocationList = []
      for (let i = 0; i < this.deliveryLocationGroups.length; i++) {
        const group = this.deliveryLocationGroups[i]
        const locationListByGroup = await this.getLocations(group)
        locationList = locationList.concat(
          locationListByGroup.filter(location => location.isActive),
        )
        if (group.locationType !== locationGroupType.POI_LOCATION) {
          mapLocationList = mapLocationList.concat(
            locationListByGroup.filter(location => location.isActive),
          )
        }
      }
      this.locationList = locationList
      this.mapLocationList = mapLocationList
    },
    async getLocations(group) {
      if (group.isSimulation) {
        return this.getLocationsById(group.locationGroupId)
      }
      return await LocationRepository.getList(
        group.locationType,
        group.locationGroupId,
      )
    },
  },
}
</script>

<style scoped>
.wrapper {
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
}
.footer {
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #cce5ff;
  padding: 10px 20px;
  min-height: 50px;
  z-index: 100;
}
</style>
